import { useKendoPopupsAboveDialog } from '@abovo/shared-ui-components';
import { ApprovalRequestService } from '@abovo/webvis-data-access';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_PDFVIEWER, PDFViewerComponent, PDFViewerPageChangeEvent } from '@progress/kendo-angular-pdfviewer';
import { firstValueFrom } from 'rxjs';

type GoogleDriveType = 'docs' | 'sheets' | 'slides' | 'drive';

interface GoogleDriveParsed {
   kind: 'google-drive';
   type: GoogleDriveType;
   label: string;
   icon: [string, string];
   docId: string;
}

interface AttachmentPdfParsed {
   kind: 'attachment-pdf';
   url: string;
}

interface GenericUrlParsed {
   kind: 'generic';
   domain: string;
   faviconUrl: string;
}

const GOOGLE_DRIVE_PATTERNS: { pattern: RegExp; type: GoogleDriveType; label: string; icon: [string, string] }[] = [
   { pattern: /docs\.google\.com\/document\/d\/([^/]+)/, type: 'docs', label: 'Google Docs', icon: ['fas', 'file-doc'] },
   { pattern: /docs\.google\.com\/spreadsheets\/d\/([^/]+)/, type: 'sheets', label: 'Google Sheets', icon: ['fas', 'file-spreadsheet'] },
   { pattern: /docs\.google\.com\/presentation\/d\/([^/]+)/, type: 'slides', label: 'Google Slides', icon: ['fas', 'file-powerpoint'] },
   { pattern: /drive\.google\.com\/file\/d\/([^/]+)/, type: 'drive', label: 'Google Drive', icon: ['fab', 'google-drive'] },
];

@Component({
   selector: 'webvis-feature-approval-url-preview',
   changeDetection: ChangeDetectionStrategy.OnPush,
   imports: [FaIconComponent, KENDO_PDFVIEWER, MatButtonModule, MatProgressBarModule, TranslatePipe],
   templateUrl: './approval-url-preview.component.html',
})
export class ApprovalUrlPreviewComponent {
   private readonly approvalRequestService = inject(ApprovalRequestService);
   private readonly http = inject(HttpClient);

   private readonly pdfViewer = viewChild(PDFViewerComponent);

   private readonly popupObserver = useKendoPopupsAboveDialog();

   /**
    * Url to preview. Must be a full url, including hostname
    */
   readonly url = input<string | null | undefined>();

   /**
    * Optional name for the file when the user downloads it, defaults to 'Download.pdf'
    */
   readonly saveFileName = input<string>();
   readonly expanded = signal(true);
   readonly currentPage = signal(1);
   readonly pageChange = output<number>();

   readonly notFound = output<boolean>();

   readonly pdfData = signal<ArrayBuffer | null>(null);
   readonly loading = signal(false);
   readonly error = signal<string | null>(null);

   readonly parsed = computed<GoogleDriveParsed | AttachmentPdfParsed | GenericUrlParsed | null>(() => {
      const raw = this.url();
      if (!raw) return null;

      for (const { pattern, type, label, icon } of GOOGLE_DRIVE_PATTERNS) {
         const match = raw.match(pattern);
         if (match) {
            return { kind: 'google-drive', type, label, icon, docId: match[1] };
         }
      }

      try {
         const urlObj = new URL(raw, window.location.origin);
         if (urlObj.origin === window.location.origin) {
            return { kind: 'attachment-pdf', url: urlObj.href };
         }
         return {
            kind: 'generic',
            domain: urlObj.hostname,
            faviconUrl: `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=32`,
         };
      } catch {
         return null;
      }
   });

   private readonly fetchEffect = effect(() => {
      const p = this.parsed();
      if (p?.kind === 'google-drive' || p?.kind === 'attachment-pdf') {
         untracked(() => void this.fetchPdf(p));
      }
   });

   async fetchPdf(p: GoogleDriveParsed | AttachmentPdfParsed) {
      this.loading.set(true);
      this.error.set(null);
      this.pdfData.set(null);

      const source = p.kind === 'google-drive' ? this.approvalRequestService.getFile(p.docId) : this.http.get(p.url, { responseType: 'arraybuffer' });

      try {
         const data = await firstValueFrom(source);
         this.pdfData.set(data);
         this.loading.set(false);
         this.notFound.emit(false);
      } catch (err) {
         const is404 = typeof err === 'object' && err !== null && 'status' in err && err.status === 404;
         this.error.set(is404 ? 'approval.file_not_found' : 'approval.preview_error');
         this.loading.set(false);
         if (is404) this.notFound.emit(true);
      }
   }

   openUrl(): void {
      if (this.parsed()?.kind === 'generic') {
         const raw = this.url();
         if (raw) window.open(raw, '_blank');
      }
   }

   onPageChange(event: PDFViewerPageChangeEvent): void {
      this.currentPage.set(event.currentPage);
      this.pageChange.emit(event.currentPage);
   }

   goToPage(page: number): void {
      const pdfViewer = this.pdfViewer();
      if (pdfViewer) {
         pdfViewer.scrollToPage(page);
         this.currentPage.set(page);
      }
   }
}
